import type { TrainingEvalRunFilters, TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { notifyWindowCapReached, windowStack, windowTaskbarTextLabel } from "@/app/windowStack";
import type { SelectedPolicyRef, TrainingEvalRunScope } from "@/modules/training/public";

// 상세·비교·병합 뷰의 종류다. 창 params 와 창 로컬 뷰 상태가 함께 쓰는 현재 도메인 타입이다.
// 그래서 이전 딥링크 모듈이 아니라 여기가 원본이다(evaluationResultsUrl 이 이 타입을 import 한다).
export type EvaluationViewMode = "detail" | "compare" | "merge";

// 이전 딥링크(view=detail|compare|merge)가 지정한 초기 뷰다. 콘텐츠는 마운트하거나 params 가
// 갱신될 때 이 값을 사용해 창 위에 해당 side panel 을 바로 연다. summary 는 여는 쪽이 이미 조회한 값이다.
export type EvaluationResultsInitialView = {
  mode: EvaluationViewMode;
  runs: readonly TrainingEvalRunSummaryView[];
};

// evaluationResults kind 엔트리의 params 계약이다. 콘텐츠(EvaluationResultsWindowContent)가 읽는다.
// Policy 카탈로그 화면과 이전 딥링크 리더가 open 할 때 채운다. 한 곳에서만 정의해 drift 를 막는다.
//
// 열 때의 조회 컨텍스트 스냅샷이다. 이후 페이지의 scope/group/member/experiment 변경은 열린 창에
// 전달되지 않는다. 창은 "열 때의 조합 결과"를 계속 보여준다.
export type EvaluationResultsWindowParams = {
  scope: TrainingEvalRunScope;
  // 정책 필터이고 창 identity(key·title)다. 창 안에서는 카드 표시에만 쓰고 바꾸지 않는다.
  policies: readonly SelectedPolicyRef[];
  // 페이지가 URL 에 보관하던 member/experiment 축의 스냅샷이다.
  catalogFilters: Pick<TrainingEvalRunFilters, "memberUuid" | "experimentId">;
  initialView?: EvaluationResultsInitialView;
};

// 정책 조합 identity 의 정규형(dedupe·정렬)이다. 창 key, 활성 창 미러, ?results_policy 리더가
// 모두 이 함수를 써야 "같은 조합 → 같은 창 → 같은 URL" 이 구조적으로 보장된다.
export function normalizedPolicyIds(ids: readonly string[]): string[] {
  return [...new Set(ids)].sort();
}

// 창 identity 다. 같은 정책 조합은 선택 순서와 관계없이 한 창이 된다. 빈 조합(이전 딥링크의
// 전체 결과)은 내용이 스코프에 의존하므로 스코프 서명을 키에 넣는다.
export function evaluationResultsWindowKey(
  policies: readonly SelectedPolicyRef[],
  scope: TrainingEvalRunScope,
): string {
  const ids = normalizedPolicyIds(policies.map((policy) => policy.policyId));
  if (ids.length === 0) {
    return scope.kind === "me" ? "all:me" : `all:team:${scope.groupUuid}`;
  }
  return ids.join("+");
}

// 창 헤더 제목과 작업표시줄 라벨 본문이 함께 쓰는 조합 이름이다.
function combinationName(policies: readonly SelectedPolicyRef[]): string | null {
  if (policies.length === 0) {
    return null;
  }
  return policies.length === 1 ? policies[0].policyName : `Policy ${policies.length}개`;
}

// Evaluation Results 를 전체화면 창 스택으로 연다. 진입점은 Policy 카탈로그 행 클릭,
// View Evaluation Results, 이전 딥링크다. 같은 정책 조합이 이미 열려 있으면 그 창을 복원한다.
// cap(5)을 넘으면 열지 않고 toast 로 안내한다.
//
// 복원할 때 scope/policies/catalogFilters 는 덮어쓰지 않는다. 창은 열 때의 스냅샷을 유지한다.
// 단 initialView 가 있는 재열기(딥링크)는 update 로 그 뷰만 전달한다. 그래서 이미 열린 창 위에
// 요청한 상세·비교를 연다.
export function openEvaluationResultsWindow(input: {
  scope: TrainingEvalRunScope;
  policies: readonly SelectedPolicyRef[];
  catalogFilters: Pick<TrainingEvalRunFilters, "memberUuid" | "experimentId">;
  initialView?: EvaluationResultsInitialView;
}): void {
  const name = combinationName(input.policies);
  const params: EvaluationResultsWindowParams = {
    scope: input.scope,
    policies: input.policies,
    catalogFilters: input.catalogFilters,
    initialView: input.initialView,
  };
  const id = windowStack.open({
    kind: "evaluationResults",
    key: evaluationResultsWindowKey(input.policies, input.scope),
    title: name === null ? "Evaluation 결과" : `Evaluation 결과 · ${name}`,
    label: windowTaskbarTextLabel("Evaluation 결과:", name ?? "전체"),
    params,
  });
  if (id === null) {
    notifyWindowCapReached();
    return;
  }
  // open 은 기존 엔트리의 params 를 갱신하지 않는다. 복원한 창(params 참조를 이 호출이 만들지
  // 않음)에 initialView 가 있으면 그 뷰만 병합한다. 스냅샷 축은 그대로 둔다.
  const entry = windowStack.getSnapshot().entries.find((candidate) => candidate.id === id);
  if (input.initialView !== undefined && entry !== undefined && entry.params !== params) {
    const existing = entry.params as EvaluationResultsWindowParams;
    windowStack.update(id, { params: { ...existing, initialView: input.initialView } });
  }
}

// 활성 창이 Evaluation Results 창이면 그 정책 조합의 정렬·dedupe 된 id 목록을 반환하고,
// 아니면 null 을 반환한다. 빈 조합(전체) 창도 null 이다. identity 가 스코프에 의존해 URL 표현이
// 없기 때문이다. Evaluation 페이지가 ?results_policy 미러링에 쓴다. 창은 app-level 이라 라우터에
// 접근할 수 없으므로 페이지가 유일한 writer 다. activeReviewDataset 과 같은 방식이다.
export function activeEvaluationResultsPolicyIds(): readonly string[] | null {
  const { entries, activeId } = windowStack.getSnapshot();
  const active = entries.find((entry) => entry.id === activeId);
  if (active === undefined || active.kind !== "evaluationResults") {
    return null;
  }
  const params = active.params as EvaluationResultsWindowParams;
  if (params.policies.length === 0) {
    return null;
  }
  return normalizedPolicyIds(params.policies.map((policy) => policy.policyId));
}
